import React, { useState } from "react";
import { Input, Button, Upload } from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  DatabaseOutlined,
  CloudUploadOutlined,
  HddOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { message } from "antd";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import Typography from "app/shared/Typography";

interface Props {
  basicInfo: any;
  setBasicInfo: (fn: (prev: any) => any) => void;
  sourceType: "DATABASE" | "UPLOAD" | null;
  setSourceType: (type: "DATABASE" | "UPLOAD" | null) => void;
  uploadedFile: File | null;
  setUploadedFile: (file: File | null) => void;
  setUploadedFileInfo: (info: any) => void;
  setUploadedFileColumns: (columns: string[]) => void;
  readOnly?: boolean;
  onReupload?: (file: File, columns: string[]) => void;
}

const parseFileColumns = (file: File): Promise<string[]> =>
  new Promise((resolve) => {
    if (file.name.endsWith(".csv") || file.type === "text/csv") {
      Papa.parse(file, {
        preview: 1,
        complete: (results) => {
          resolve((results.data[0] as string[]).filter(Boolean));
        },
        error: () => resolve([]),
      });
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target?.result, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1 });
          resolve(((rows[0] as string[]) || []).filter(Boolean));
        } catch {
          resolve([]);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  });

const InformationFlowTab: React.FC<Props> = ({
  basicInfo,
  setBasicInfo,
  sourceType,
  setSourceType,
  uploadedFile,
  setUploadedFile,
  setUploadedFileInfo,
  setUploadedFileColumns,
  readOnly = false,
  onReupload,
}) => {
  const [uploadingFile, setUploadingFile] = useState(false);
  const requiredMark = (
    <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>
  );

  const handleFileUpload = async (file: File) => {
    const validMimeTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv",
    ];
    if (!validMimeTypes.includes(file.type)) {
      message.error(
        "Invalid file format. Only Excel (.xlsx/.xls) or CSV is allowed.",
      );
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("filename", file.name);
      formData.append("fileType", file.type);
      formData.append("clientId", basicInfo.client_id || "");

      const response = await fetch(
        "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/uploadExcel",
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            workspaceId: localStorage.getItem("currentWorkspace") || "",
          },
          body: formData,
        },
      );

      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || `Upload failed (${response.status})`);

      setUploadedFile(file);
      setUploadedFileInfo({
        message: data.message,
        insertedCount: data.insertedCount,
        filename: file.name,
      });
      const columns = await parseFileColumns(file);
      setUploadedFileColumns(columns);
      message.success(
        data.insertedCount
          ? `${data.message || "Uploaded!"} ${data.insertedCount} records inserted.`
          : data.message || "File uploaded successfully!",
      );
      setBasicInfo((prev) => ({
        ...prev,
        database: "email_followup_automation",
        collection: prev.name
          ? prev.name.replace(/\s+/g, "_")
          : "uploaded_data",
      }));
    } catch (error) {
      message.error(
        `Failed to upload: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
      setUploadedFile(null);
      setUploadedFileInfo(null);
    } finally {
      setUploadingFile(false);
    }
  };

  return (
    <div className="FormContainer">
      <div
        style={{
          borderLeft: "3px solid #0a8394",
          paddingLeft: 12,
          marginBottom: 20,
        }}
      >
        <Typography variant="h5" weight={600}>
          Basic Information
        </Typography>
      </div>
      <div
        style={{
          border: "1px solid #e8e8e8",
          borderRadius: 12,
          padding: "20px 24px",
          background: "#fafafa",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          marginBottom: 20,
        }}
      >
        {/* 1. FollowUp Name + Client ID */}
        <div className="FormRow" style={{ marginBottom: 20 }}>
          <div className="FormGroup">
            <Typography variant="xs" style={{ marginBottom: 3 }}>
              FollowUp Name {requiredMark}
            </Typography>
            <Input
              placeholder="ie: Yatra follow-up EY"
              style={{ height: 38 }}
              value={basicInfo.name}
              disabled={readOnly}
              onChange={(e) => {
                const newName = e.target.value;
                const clientId = newName
                  .trim()
                  .toUpperCase()
                  .replace(/[\s\-]+/g, "_")
                  .replace(/[^A-Z0-9_]/g, "");
                setBasicInfo((prev) => ({
                  ...prev,
                  name: newName,
                  client_id: clientId,
                  ...(uploadedFile && {
                    collection: newName ? newName.replace(/\s+/g, "_") : "uploaded_data",
                  }),
                }));
              }}
            />
          </div>
          <div className="FormGroup">
            <Typography variant="xs" style={{ marginBottom: 3 }}>
              Client ID {requiredMark}
            </Typography>
            <Input
              placeholder="ie: YATRA_FOLLOWUP_EY"
              style={{ height: 38 }}
              value={basicInfo.client_id}
              disabled
            />
          </div>
        </div>

        {/* 2. Source Type Cards */}
        <div style={{ marginBottom: 20 }}>
          <Typography variant="xs" style={{ marginBottom: 8 }}>
            Source Type {requiredMark}
          </Typography>
          <div className="source-type-cards">
            <div
              className={`source-card ${sourceType === "DATABASE" ? "selected" : ""} ${readOnly ? "disabled" : ""}`}
              onClick={() => {
                if (readOnly) return;
                setSourceType("DATABASE");
                setBasicInfo((prev) => ({
                  ...prev,
                  databaseType: "",
                  collection: "",
                  database: "unified-hotel-followup",
                }));
              }}
            >
              <DatabaseOutlined className="source-card-icon" />
              <div>
                <Typography variant="body" weight={600}>
                  Database Connection
                </Typography>
                <Typography variant="xs" style={{ color: "#595959" }}>
                  Sync from enterprise databases like Postgres or MongoDB.
                </Typography>
              </div>
              <div className="source-card-radio">
                <div
                  className={
                    sourceType === "DATABASE" ? "radio-filled" : "radio-empty"
                  }
                />
              </div>
            </div>

            <div
              className={`source-card ${sourceType === "UPLOAD" ? "selected" : ""} ${readOnly ? "disabled" : ""}`}
              onClick={() => {
                if (readOnly) return;
                setSourceType("UPLOAD");
              }}
            >
              <CloudUploadOutlined className="source-card-icon" />
              <div>
                <Typography variant="body" weight={600}>
                  File Upload
                </Typography>
                <Typography variant="xs" style={{ color: "#595959" }}>
                  Upload CSV or Excel files for one-time or batch processing.
                </Typography>
              </div>
              <div className="source-card-radio">
                <div
                  className={
                    sourceType === "UPLOAD" ? "radio-filled" : "radio-empty"
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* 2a. DB Type as cards — when DATABASE selected */}
        {sourceType === "DATABASE" && (
          <>
            <div style={{ marginBottom: 20 }}>
              <Typography variant="xs" style={{ marginBottom: 8 }}>
                DB Type {requiredMark}
              </Typography>
              <div className="source-type-cards">
                <div
                  className={`source-card ${basicInfo.databaseType === "MONGO" ? "selected" : ""} ${readOnly ? "disabled" : ""}`}
                  onClick={() => {
                    if (readOnly) return;
                    setBasicInfo((prev) => ({
                      ...prev,
                      databaseType: "MONGO",
                      collection: "",
                      database: "unified-hotel-followup",
                    }));
                  }}
                >
                  <HddOutlined className="source-card-icon" />
                  <div>
                    <Typography variant="body" weight={600}>
                      MongoDB
                    </Typography>
                    <Typography variant="xs" style={{ color: "#595959" }}>
                      Connect to a MongoDB database collection.
                    </Typography>
                  </div>
                  <div className="source-card-radio">
                    <div
                      className={
                        basicInfo.databaseType === "MONGO"
                          ? "radio-filled"
                          : "radio-empty"
                      }
                    />
                  </div>
                </div>

                <div
                  className={`source-card ${basicInfo.databaseType === "POSTGRE" ? "selected" : ""} ${readOnly ? "disabled" : ""}`}
                  onClick={() => {
                    if (readOnly) return;
                    setBasicInfo((prev) => ({
                      ...prev,
                      databaseType: "POSTGRE",
                      collection: "",
                      database: "unified-hotel-followup",
                    }));
                  }}
                >
                  <TableOutlined className="source-card-icon" />
                  <div>
                    <Typography variant="body" weight={600}>
                      PostgreSQL
                    </Typography>
                    <Typography variant="xs" style={{ color: "#595959" }}>
                      Connect to a PostgreSQL database table.
                    </Typography>
                  </div>
                  <div className="source-card-radio">
                    <div
                      className={
                        basicInfo.databaseType === "POSTGRE"
                          ? "radio-filled"
                          : "radio-empty"
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {basicInfo.databaseType && (
              <div className="FormRow">
                <div className="FormGroup">
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    {basicInfo.databaseType === "MONGO"
                      ? "Collection Name"
                      : "Table Name"}{" "}
                    {requiredMark}
                  </Typography>
                  <Input
                    placeholder={
                      basicInfo.databaseType === "MONGO"
                        ? "ie: hotel_bookings"
                        : "ie: hotel_booking"
                    }
                    style={{ height: 38 }}
                    value={basicInfo.collection}
                    disabled={readOnly}
                    onChange={(e) =>
                      setBasicInfo((prev) => ({
                        ...prev,
                        collection: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* 2b. File Upload — when UPLOAD selected */}
        {sourceType === "UPLOAD" && (
          <div className="FormRow" style={{ marginBottom: 20 }}>
            <div className="FormGroup" style={{ width: "100%" }}>
              {uploadedFile ? (
                <div className="uploaded-file-row">
                  <UploadOutlined style={{ color: "#0a8394", fontSize: 16 }} />
                  <Typography
                    variant="xs"
                    style={{ flex: 1, color: "#262626" }}
                  >
                    {uploadedFile.name}
                  </Typography>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setUploadedFile(null);
                      setUploadedFileInfo(null);
                      setBasicInfo((prev) => ({
                        ...prev,
                        database: "",
                        collection: "",
                      }));
                    }}
                    style={{ padding: 0 }}
                  />
                </div>
              ) : (
                <Upload.Dragger
                  beforeUpload={async (file) => {
                    if (readOnly && onReupload) {
                      const columns = await parseFileColumns(file);
                      onReupload(file, columns);
                    } else {
                      handleFileUpload(file);
                    }
                    return false;
                  }}
                  showUploadList={false}
                  accept=".csv,.xlsx,.xls"
                  disabled={uploadingFile}
                  className="file-dragger"
                >
                  <p className="ant-upload-drag-icon">
                    <CloudUploadOutlined
                      style={{ fontSize: 36, color: "#0a8394" }}
                    />
                  </p>
                  <p
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#262626",
                      marginBottom: 4,
                    }}
                  >
                    {uploadingFile
                      ? "Uploading..."
                      : readOnly && onReupload
                      ? "Re-upload to update data"
                      : "Click or drag file to upload"}
                  </p>
                  <p style={{ fontSize: 12, color: "#8c8c8c" }}>
                    Supports CSV, XLS, XLSX
                  </p>
                </Upload.Dragger>
              )}
            </div>
          </div>
        )}

      </div>{" "}
      {/* end card */}
    </div>
  );
};

export default InformationFlowTab;
